import { get, isEmpty, merge, noop } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';

import { i18n } from '@lingui/core';
import useUiAlerts, { createAlertConfig } from '../stores/AlertsStore';
import { useDataService } from './ApiDataService';
import { useGenericError } from './FormErrorService';
import { getSuccessToast } from '../utilities/FormUtilities';
import useUiToasts, { WARNING } from '../stores/ToastStore';
import { useNavigate } from '../utilities/PgmUtilities';
import {
    TEST_STATUS_DISABLED,
    TEST_STATUS_RUNNING,
} from '../components/pages-admin/service-tests/ServiceTestAdminConstants';

const ENABLE_SUCCESS = t`Successfully Enabled`;
const DISABLE_SUCCESS = t`Successfully Disabled`;
const DELETE_SUCCESS = t`Successfully Deleted`;
const RESCAN_SUCCESS = t`The selected devices will be rescanned within a few minutes`;
const SCHEDULE_SUCCESS = t`Successfully Updated`;

export const SERVICE_TESTS_API_BASE = '/ngp/v2/service-tests';

export const TESTS_LIST_UPDATE_ENDPOINT = '/ipm/v1/admin/tests/selection/update';
export const TESTS_DETAIL_UPDATE_ENDPOINT = '/ngp/v2/service-tests/';
export const TESTS_CACHE_ENDPOINT = '/ipm/v1/open/tests/cache';

export const ENABLE = 'Enable';
export const DELETE = 'Delete';
export const PRE_DELETE = 'PreDelete';
export const DISABLE = 'Disable';
export const CREATE = 'Create';
export const IMPORT = 'Import';
export const RESCAN = 'Rescan';
export const SCHEDULE = 'Schedule';
export const COPY = 'Copy';
export const VERIFY = 'Verify';
export const ACTION_SUCCESS_MESSAGES = {
    [ENABLE]: ENABLE_SUCCESS,
    [DISABLE]: DISABLE_SUCCESS,
    [DELETE]: DELETE_SUCCESS,
    [RESCAN]: RESCAN_SUCCESS,
    [SCHEDULE]: SCHEDULE_SUCCESS,
};

export const SERVICE_TESTS_UPDATE_DOC_STATUS = {
    [ENABLE]: { status: 'Running' },
    [DISABLE]: { status: 'Disabled' },
};

export const EMPTY_BUTTONS = {};

const useSuccessfulDataTableAction = (successMessageObj) => {
    const [, uiToastActions] = useUiToasts();
    const navigate = useNavigate();

    const navigateAway = (goTo, reloadFunc) => {
        if (isEmpty(goTo)) {
            return reloadFunc();
        }

        return navigate(goTo, { replace: true });
    };

    return (response, onActionGoTo, action, needAlert = true, refresherFunc = noop, nextActionHandler = noop) => {
        if (needAlert) {
            let successToast = getSuccessToast(action, successMessageObj);
            if (RESCAN === action) {
                const matchedCount = get(response, 'matchedCount', 0);
                const requestedCount = get(response, 'requestedCount', 0);
                if (matchedCount !== requestedCount) {
                    const notMatchedCountMsg = i18n._(
                        t`Eligible selected devices will be rescanned within a few minutes. Some devices did not meet rescan criteria. See help for more information.`
                    );
                    successToast = createAlertConfig(WARNING, notMatchedCountMsg);
                }
            } else if (PRE_DELETE === action) {
                return nextActionHandler(response);
            }

            uiToastActions.createToast(merge(successToast, { createOnNavigate: !isEmpty(onActionGoTo) }));
        }
        navigateAway(onActionGoTo, refresherFunc);
        return response;
    };
};

export function useDataTableSelectionState() {
    const [tableSelection, setTableSelection] = useState(undefined);
    const [dataTableActionsDisabled, setDataTableActionsDisabled] = useState(false);
    const currentTableSelectionAndRows = useRef(null);

    const handleTableSelectionChange = useCallback((currentTableSelection, totalRows, selectionLength) => {
        setTableSelection(currentTableSelection);

        const hasSelection =
            currentTableSelection && (!isEmpty(currentTableSelection.selection) || currentTableSelection.inverted);
        setDataTableActionsDisabled(!hasSelection);

        currentTableSelectionAndRows.current = {
            currentTableSelection,
            totalRows,
            selectionLength,
        };
    }, []);

    return {
        dataTableActionsDisabled,
        tableSelection,
        currentTableSelectionAndRows,
        handleTableSelectionChange,
    };
}

/**
 * Hook for creating post-action handlers with consistent success/error handling
 * @param {Function} handleSuccessfulDataTableAction - Success handler
 *   response,
 *   onDataTableActionGoTo,
 *   action,
 *   needAlert,
 *   refresherFunc,
 *   nextActionHandler
 * @param {Function} refresher - Table refresh function
 * @param {Function} nextActionHandler - Handler for chained actions (e.g., pre-delete -> delete)
 * @returns {Function} Factory function that creates action-specific handlers
 */
export function usePostDataTableActionHandler(handleSuccessfulDataTableAction, refresher, nextActionHandler = noop) {
    return useCallback(
        (action, needAlert = false) =>
            (response) =>
                handleSuccessfulDataTableAction(response, null, action, needAlert, refresher, nextActionHandler),
        [handleSuccessfulDataTableAction, refresher, nextActionHandler]
    );
}

export const useFormDataTableActionWrapper = () => {
    const [, uiAlertActions] = useUiAlerts();
    const handleGenericError = useGenericError();
    const handleSuccessfulDataTableAction = useSuccessfulDataTableAction(ACTION_SUCCESS_MESSAGES);
    const [dataTableActioning, setDataTableActioning] = useState(false);

    const dataTableActionWrapper = (callerConfig) => {
        const {
            dataTableActionFunction,
            onDataTableActionGoTo,
            action,
            needAlert,
            refresherFunc = noop,
            nextActionHandler = noop,
            postDataTableActionHandler,
            errorHandler = handleGenericError,
        } = callerConfig;

        const resolvedPostDataTableActionHandler =
            postDataTableActionHandler ||
            ((response) =>
                handleSuccessfulDataTableAction(
                    response,
                    onDataTableActionGoTo,
                    action,
                    needAlert,
                    refresherFunc,
                    nextActionHandler
                ));

        return () =>
            Promise.resolve(setDataTableActioning(true))
                .then(() => uiAlertActions.handleRemove())
                .then(dataTableActionFunction)
                .then(resolvedPostDataTableActionHandler)
                .catch(errorHandler)
                .finally(() => setDataTableActioning(false));
    };

    return {
        dataTableActioning,
        dataTableActionWrapper,
        handleSuccessfulDataTableAction,
    };
};

/**
 * Factory for creating data table action service with API integration
 * @param {Object} dataService - Data service for making API calls
 * @returns {Object} Service methods for data table actions
 */
const FormDataTableActionServiceFactory = (dataService) => {
    /**
     * Execute POST action with optional error handler
     * @param {string} url - API endpoint URL
     * @param {Object} body - Request body
     * @param {Object} params - Query parameters
     * @param {Function} errorHandler - Optional error handler for delete operations
     * @param {Function} postDataTableActionHandler - Success handler
     * @returns {Promise} API response promise
     */
    const dataServicePostAction = (url, body, params, errorHandler, postDataTableActionHandler) => {
        if (SERVICE_TESTS_API_BASE === url) {
            return dataService.remove(url, body, params, null, errorHandler).then(postDataTableActionHandler);
        }

        return dataService.post(url, body, params, null, errorHandler).then(postDataTableActionHandler);
    };

    /**
     * Execute list selection action (e.g., bulk enable/disable/delete)
     * @param {string} url - API endpoint URL
     * @param {Object} params - Query parameters
     * @param {Object} tableSelection - Selected rows data
     * @param {Function} errorHandler - Error handler for delete operations
     * @param {Function} postDataTableActionHandler - Success handler
     * @returns {Promise} API response promise
     */
    const dataTableActionListSelection = (
        url,
        params,
        tableSelection,
        errorHandler,
        postDataTableActionHandler = (response) => response
    ) => dataServicePostAction(url, tableSelection, params, errorHandler, postDataTableActionHandler);

    /**
     * Execute detail selection action (e.g., single row actions)
     * @param {string} action - action
     * @param {string} url - API endpoint URL
     * @param {Object} params - Query parameters
     * @param {string} recordId - string of selected IDs
     * @param {boolean} tableSelectionAction - use ids or selection as action post body
     * @param {Function} errorHandler - Optional error handler for operations
     * @param {Function} postDataTableActionHandler - Success handler
     * @returns {Promise} API response promise
     */
    const dataTableActionDetailSelection = (
        action,
        url,
        params,
        recordId,
        tableSelectionAction,
        errorHandler,
        postDataTableActionHandler = (response) => response
    ) => {
        // For Service Tests Detail action, after action, would call get /ipm/v1/open/tests/cache
        if (url === TESTS_DETAIL_UPDATE_ENDPOINT) {
            const testsDetailActionUrl = `${TESTS_DETAIL_UPDATE_ENDPOINT}/${recordId}`;

            if (action === DISABLE || action === ENABLE) {
                let status = TEST_STATUS_DISABLED;
                if (action === DISABLE) {
                    status = TEST_STATUS_DISABLED;
                } else if (action === ENABLE) {
                    status = TEST_STATUS_RUNNING;
                }
                const payload = {
                    status,
                };
                return dataService
                    .patch(testsDetailActionUrl, payload)
                    .then(() => dataService.get(TESTS_CACHE_ENDPOINT, { t: Date.now() }))
                    .then(postDataTableActionHandler);
            }

            if (action === DELETE) {
                return dataService
                    .remove(testsDetailActionUrl)
                    .then(() => dataService.get(TESTS_CACHE_ENDPOINT, { t: Date.now() }))
                    .then(postDataTableActionHandler);
            }
        }

        let body;
        if (tableSelectionAction) {
            body = {
                selection: [recordId],
                inverted: false,
            };
        } else {
            body = { ids: recordId };
        }
        return dataServicePostAction(url, body, params, errorHandler, postDataTableActionHandler);
    };

    return {
        dataTableActionListSelection,
        dataTableActionDetailSelection,
    };
};

export const useDataTableActionService = () => {
    const dataService = useDataService();
    return useMemo(() => FormDataTableActionServiceFactory(dataService), [dataService]);
};

export default FormDataTableActionServiceFactory;
